import { useQueryClient } from "@tanstack/react-query";
import { KeyRound, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { ApiRequestError, useApi } from "../api/client";
import { qk } from "../api/queries";
import type { RestorePointDay, TotpSetup } from "../api/types";
import { Brand } from "../app/Brand";
import { LocaleMenu } from "../app/LocaleMenu";
import { API_MODE } from "../app/mode";
import { common } from "../i18n/common";
import { useT } from "../i18n/core";
import { CodeBlock } from "../ui/Common";
import { QrCode } from "../ui/QrCode";
import { RestoreStrip } from "../ui/RestoreStrip";
import { loginMessages } from "./login.i18n";
import { resetMessages } from "./reset.i18n";

// Régua decorativa de exemplo no painel lateral.
const sample: RestorePointDay[] = Array.from({ length: 30 }, (_, i) => ({
  date: `2026-08-${String(i + 1).padStart(2, "0")}`,
  state: i === 11 ? "failed" : i === 18 ? "partial" : (i + 2) % 7 === 0 || (i + 1) % 7 === 0 ? "none" : "ok",
}));

export function LoginPage() {
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useT(loginMessages);
  const tc = useT(common);
  const tr = useT(resetMessages);
  const [step, setStep] = useState<"password" | "totp" | "enroll">("password");
  const [email, setEmail] = useState(API_MODE === "mock" ? "alan@nuvemserra.com.br" : "");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [challenge, setChallenge] = useState("");
  // Conta sem autenticador: segredo novo, válido só para este desafio (ADR 0020).
  const [setup, setSetup] = useState<TotpSetup | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.login(email, password);
      setChallenge(r.challengeId);
      setSetup(r.totpSetup ?? null);
      setCode("");
      setStep(r.totpSetup ? "enroll" : "totp");
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.loginFailed);
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // Mesmo endpoint nos dois casos: com cadastro pendente, o código confirma o autenticador.
      const s = await api.verifyTotp(challenge, code);
      qc.setQueryData(qk.session, s);
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from && from !== "/login" ? from : s.homeOrgId ? `/o/${s.homeOrgId}/overview` : "/", { replace: true });
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.totpFailed);
    } finally {
      setBusy(false);
    }
  }

  function backToPassword() {
    // Descarta o desafio e o segredo exibido; um novo login gera outros.
    setStep("password");
    setChallenge("");
    setSetup(null);
    setCode("");
    setError(null);
  }

  const codeField = (id: string, label: string) => (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id} className="input code" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
        value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} autoFocus={step === "totp"} required
      />
      {step === "totp" && <span className="hint">{tr.lostAuthenticator}</span>}
      {API_MODE === "mock" && <span className="hint">{t.mockCode}</span>}
    </div>
  );

  return (
    <div className="login">
      <aside className="login-side">
        <Brand />
        <div className="login-quote">
          <h2>{t.headline}</h2>
          <p>{t.pitch}</p>
        </div>
        <div className="login-strip" aria-hidden="true">
          <span className="eyebrow" style={{ color: "#8f94c4" }}>{t.stripCaption}</span>
          <RestoreStrip days={sample} size="big" />
        </div>
      </aside>

      <div className="login-form">
        <div className="login-lang"><LocaleMenu variant="plain" /></div>
        {step === "password" ? (
          <form className="login-card" onSubmit={submitPassword} noValidate>
            <div>
              <h1>{t.title}</h1>
              <p className="ink-2" style={{ marginTop: 6 }}>{t.lede}</p>
            </div>
            <div className="field">
              <label htmlFor="login-email">{t.email}</label>
              <input id="login-email" className="input" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="field">
              <label htmlFor="login-password">{t.password}</label>
              <input id="login-password" className="input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              {API_MODE === "mock" && <span className="hint">{t.mockPassword} {t.mockEnroll}</span>}
            </div>
            {error && <p className="field error" role="alert">{error}</p>}
            <button className="btn btn-primary" type="submit" disabled={busy} style={{ height: 40 }}>
              <KeyRound aria-hidden="true" /> {busy ? t.checking : t.continue}
            </button>
            <button className="btn btn-ghost" type="button" onClick={() => navigate("/reset")}>{tr.forgot}</button>
            <p className="ink-2" style={{ textAlign: "center" }}>
              {t.noAccount} <Link to="/signup">{t.trialLink}</Link>
            </p>
          </form>
        ) : step === "enroll" && setup ? (
          <form className="login-card" onSubmit={submitCode} noValidate aria-labelledby="login-enroll-title">
            <div>
              <h1 id="login-enroll-title">{t.enrollTitle}</h1>
              <p className="ink-2" style={{ marginTop: 6 }}>{t.enrollLede}</p>
            </div>
            <div style={{ display: "grid", justifyItems: "center", gap: 8 }}>
              <QrCode value={setup.otpauthUri} label={t.qrLabel} />
              <span className="muted" style={{ fontSize: "var(--fs-12)", textAlign: "center", overflowWrap: "anywhere" }}>{t.enrollAccount(setup.email)}</span>
            </div>
            <div className="field">
              <span className="hint">{t.manualKey}</span>
              <CodeBlock code={setup.secret} label={t.secretLabel} />
            </div>
            {codeField("login-enroll-code", t.enrollCode)}
            <p className="muted" style={{ fontSize: "var(--fs-12)" }}>{t.enrollExpiry}</p>
            {error && <p className="field error" role="alert">{error}</p>}
            <button className="btn btn-primary" type="submit" disabled={busy || code.length !== 6} style={{ height: 40 }}>
              <ShieldCheck aria-hidden="true" /> {busy ? t.validating : t.enrollSubmit}
            </button>
            <button className="btn btn-ghost" type="button" onClick={backToPassword}>{tc.back}</button>
          </form>
        ) : (
          <form className="login-card" onSubmit={submitCode} noValidate>
            <div>
              <h1>{t.totpTitle}</h1>
              <p className="ink-2" style={{ marginTop: 6 }}>{t.totpLede}</p>
            </div>
            {codeField("login-totp", t.code)}
            {error && <p className="field error" role="alert">{error}</p>}
            <button className="btn btn-primary" type="submit" disabled={busy || code.length !== 6} style={{ height: 40 }}>
              <ShieldCheck aria-hidden="true" /> {busy ? t.validating : t.signIn}
            </button>
            <button className="btn btn-ghost" type="button" onClick={backToPassword}>{tc.back}</button>
          </form>
        )}
      </div>
    </div>
  );
}
